// Phase 9B — Trust Center styles.
//
// Token-only CSS. No hex literals. Premium design system.

import type { CSSProperties } from "react";
import { cardLow, cardMedium } from "../../styles/elevation.ts";
import { transitionStandard, transitionFast } from "../../styles/motion.ts";

// ─── Shell ────────────────────────────────────────────────────

export const trustRoot: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "var(--sz-space-4)",
};

export const trustHeader: CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "baseline",
};

export const trustTitle: CSSProperties = {
  fontFamily: "var(--sz-font-display)",
  fontSize: "clamp(1.4rem, 3vw, 2rem)",
  letterSpacing: "3px",
  textTransform: "uppercase",
  color: "var(--sz-text-primary)",
  lineHeight: 1,
};

export const trustSubtitle: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-xs)",
  letterSpacing: "2px",
  textTransform: "uppercase",
  color: "var(--sz-text-muted)",
  marginTop: "4px",
};

// ─── Trust metrics row ────────────────────────────────────────

export const metricsRow: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: "var(--sz-space-3)",
};

export const metricCard: CSSProperties = {
  ...cardMedium,
  display: "flex",
  flexDirection: "column",
  gap: "var(--sz-space-1)",
  padding: "var(--sz-space-4)",
  borderRadius: "var(--sz-radius-md)",
  background: "var(--sz-surface-card)",
  borderTop: "3px solid var(--sz-amber)",
};

export const metricValue: CSSProperties = {
  fontFamily: "var(--sz-font-display)",
  fontSize: "clamp(1.6rem, 3vw, 2.4rem)",
  letterSpacing: "1px",
  color: "var(--sz-text-primary)",
  lineHeight: 1,
};

export const metricLabel: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "9px",
  letterSpacing: "2px",
  textTransform: "uppercase",
  color: "var(--sz-text-muted)",
};

export const metricDesc: CSSProperties = {
  fontFamily: "var(--sz-font-body)",
  fontSize: "var(--sz-body-xs)",
  color: "var(--sz-text-muted)",
  lineHeight: 1.4,
  marginTop: "var(--sz-space-1)",
};

// ─── Article type pills ───────────────────────────────────────

const typeBase: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "9px",
  letterSpacing: "1px",
  textTransform: "uppercase",
  padding: "2px 8px",
  borderRadius: "var(--sz-radius-sm)",
};

export const TYPE_STYLES: Record<string, CSSProperties> = {
  methodology: { ...typeBase, background: "rgba(245, 158, 11, 0.12)", color: "var(--sz-amber)" },
  trust_policy: { ...typeBase, background: "rgba(34, 197, 94, 0.12)", color: "var(--sz-success-text)" },
  faq: { ...typeBase, background: "rgba(59, 130, 246, 0.12)", color: "var(--sz-info-text)" },
  glossary: { ...typeBase, background: "var(--sz-surface-recessed)", color: "var(--sz-text-secondary)" },
  tutorial: { ...typeBase, background: "rgba(168, 85, 247, 0.12)", color: "var(--sz-text-secondary)" },
  release_note: { ...typeBase, background: "rgba(239, 68, 68, 0.12)", color: "var(--sz-error-text)" },
  system_doc: { ...typeBase, background: "var(--sz-surface-recessed)", color: "var(--sz-text-muted)" },
};

// ─── Article cards ────────────────────────────────────────────

export const articlesGrid: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
  gap: "var(--sz-space-3)",
};

export const articleCard: CSSProperties = {
  ...cardLow,
  ...transitionStandard,
  display: "flex",
  flexDirection: "column",
  gap: "var(--sz-space-2)",
  padding: "var(--sz-space-4)",
  borderRadius: "var(--sz-radius-md)",
  background: "var(--sz-surface-card)",
  cursor: "pointer",
};

export const articleCardPinned: CSSProperties = {
  ...articleCard,
  borderLeft: "3px solid var(--sz-amber)",
};

export const articleTitle: CSSProperties = {
  fontFamily: "var(--sz-font-display)",
  fontSize: "1.1rem",
  letterSpacing: "1px",
  textTransform: "uppercase",
  color: "var(--sz-text-primary)",
  lineHeight: 1.2,
};

export const articleSummary: CSSProperties = {
  fontFamily: "var(--sz-font-body)",
  fontSize: "var(--sz-body-sm)",
  color: "var(--sz-text-secondary)",
  lineHeight: 1.5,
};

export const articleMeta: CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "var(--sz-space-2)",
};

export const articleCategory: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "9px",
  letterSpacing: "1px",
  textTransform: "uppercase",
  color: "var(--sz-text-muted)",
};

export const articleTag: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "8px",
  letterSpacing: "0.5px",
  textTransform: "uppercase",
  padding: "1px 5px",
  borderRadius: "var(--sz-radius-sm)",
  background: "var(--sz-surface-recessed)",
  color: "var(--sz-text-muted)",
};

// ─── Article detail ───────────────────────────────────────────

export const articleDetail: CSSProperties = {
  ...cardLow,
  display: "flex",
  flexDirection: "column",
  gap: "var(--sz-space-3)",
  padding: "var(--sz-space-5)",
  borderRadius: "var(--sz-radius-md)",
  background: "var(--sz-surface-card)",
};

export const articleContent: CSSProperties = {
  fontFamily: "var(--sz-font-body)",
  fontSize: "var(--sz-body-md)",
  color: "var(--sz-text-primary)",
  lineHeight: 1.7,
  whiteSpace: "pre-wrap",
};

export const backButton: CSSProperties = {
  ...transitionFast,
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-xs)",
  letterSpacing: "1px",
  textTransform: "uppercase",
  padding: "var(--sz-space-1) var(--sz-space-2)",
  background: "transparent",
  borderTop: "none",
  borderRight: "none",
  borderBottom: "none",
  borderLeft: "none",
  color: "var(--sz-amber)",
  cursor: "pointer",
};

// ─── Tabs ─────────────────────────────────────────────────────

export const tabBar: CSSProperties = {
  display: "flex",
  gap: "2px",
  borderBottom: "1px solid var(--sz-border-subtle)",
};

export const tabItem: CSSProperties = {
  ...transitionFast,
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-xs)",
  letterSpacing: "1px",
  textTransform: "uppercase",
  padding: "var(--sz-space-2) var(--sz-space-3)",
  background: "transparent",
  borderTop: "none",
  borderRight: "none",
  borderLeft: "none",
  borderBottom: "2px solid transparent",
  color: "var(--sz-text-muted)",
  cursor: "pointer",
};

export const tabItemActive: CSSProperties = {
  ...tabItem,
  color: "var(--sz-text-primary)",
  borderBottom: "2px solid var(--sz-amber)",
};

// ─── Section ──────────────────────────────────────────────────

export const sectionHeader: CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "baseline",
};

export const sectionTitle: CSSProperties = {
  fontFamily: "var(--sz-font-display)",
  fontSize: "1.2rem",
  letterSpacing: "2px",
  textTransform: "uppercase",
  color: "var(--sz-text-primary)",
};

export const sectionCount: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-xs)",
  letterSpacing: "1px",
  color: "var(--sz-text-muted)",
};

// ─── Empty ────────────────────────────────────────────────────

export const emptyState: CSSProperties = {
  ...cardLow,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: "var(--sz-space-2)",
  padding: "var(--sz-space-6) var(--sz-space-4)",
  borderRadius: "var(--sz-radius-md)",
  background: "var(--sz-surface-card)",
};

export const emptyIcon: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "2rem",
  color: "var(--sz-text-muted)",
  opacity: 0.5,
};

export const emptyTitle: CSSProperties = {
  fontFamily: "var(--sz-font-display)",
  fontSize: "1.1rem",
  letterSpacing: "2px",
  textTransform: "uppercase",
  color: "var(--sz-text-secondary)",
};

export const emptyNote: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-xs)",
  color: "var(--sz-text-muted)",
  textAlign: "center",
  maxWidth: "360px",
};

export const phantomBadge: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "9px",
  letterSpacing: "2px",
  textTransform: "uppercase",
  color: "var(--sz-text-muted)",
  opacity: 0.5,
  textAlign: "center",
  padding: "var(--sz-space-2) 0",
};
